import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { Button } from '../button/button.tsx';
import { Input } from '../input/input.tsx';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../dialog/dialog.tsx';

/* What the browser refused itself, before anything was sent. */
type Rejection = 'too_large' | 'wrong_type';

export interface ImageUploadDialogProps {
  title: string;
  closeLabel: string;
  /* The current picture, at a size worth looking at. */
  preview: ReactNode;
  /* What a file may be, e.g. "JPG, PNG or WebP, up to 1 MB." */
  hint: string;
  acceptTypes: readonly string[];
  maxBytes: number;
  tooLargeMessage: string;
  wrongTypeMessage: string;
  /* The caller's own request failed; null or absent when it did not. */
  failedMessage?: string | null;
  uploadLabel: string;
  uploading?: boolean;
  /* Called only with a file that passed the type and size checks. */
  onUpload: (file: File) => void;
  removeLabel: string;
  removing?: boolean;
  /* Absent when there is nothing to remove. */
  onRemove?: () => void;
  /* Further actions, placed before remove and upload. */
  actions?: ReactNode;
  /* Another action of the caller's is in flight; holds both buttons. */
  busy?: boolean;
  onClose: () => void;
}

/**
 * A picture and the things you can do to it: replace it, remove it. Type and
 * size are checked here, before anything is sent; the server checks the bytes
 * again. It stays open across both, so the result is what you are looking at.
 */
export function ImageUploadDialog({
  title,
  closeLabel,
  preview,
  hint,
  acceptTypes,
  maxBytes,
  tooLargeMessage,
  wrongTypeMessage,
  failedMessage,
  uploadLabel,
  uploading = false,
  onUpload,
  removeLabel,
  removing = false,
  onRemove,
  actions,
  busy = false,
  onClose,
}: ImageUploadDialogProps) {
  const fileField = useRef<HTMLInputElement>(null);
  const [rejected, setRejected] = useState<Rejection | null>(null);

  const held = busy || uploading || removing;
  const problem =
    rejected === 'too_large'
      ? tooLargeMessage
      : rejected === 'wrong_type'
        ? wrongTypeMessage
        : (failedMessage ?? null);

  function choose(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    /* Emptied at once, so picking the same file again after a fix still counts as a change. */
    event.target.value = '';
    if (!file) return;

    if (!acceptTypes.includes(file.type)) {
      setRejected('wrong_type');
      return;
    }

    if (file.size > maxBytes) {
      setRejected('too_large');
      return;
    }

    setRejected(null);
    onUpload(file);
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogClose aria-label={closeLabel} />
        </DialogHeader>

        <div className='flex flex-col items-center gap-3 px-5 py-5'>
          {preview}

          {/* A refusal replaces the rule: the rule and why it was broken are one sentence. */}
          {problem ? (
            <p role='alert' className='text-sm text-danger'>
              {problem}
            </p>
          ) : (
            <p className='text-sm text-ink-muted'>{hint}</p>
          )}
        </div>

        {/* Opened by the button below: the native control cannot be styled. */}
        <Input
          ref={fileField}
          type='file'
          accept={acceptTypes.join(',')}
          className='hidden'
          aria-label={title}
          onChange={choose}
        />

        <DialogFooter>
          {actions}

          {onRemove && (
            <Button
              size='sm'
              variant='dangerQuiet'
              disabled={held}
              loading={removing}
              onClick={onRemove}
            >
              {removeLabel}
            </Button>
          )}

          <Button
            size='sm'
            disabled={held}
            loading={uploading}
            onClick={() => fileField.current?.click()}
          >
            {uploadLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
